import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches render-time errors in the subtree so a single screen crash shows a readable
 * message instead of blanking the whole SPA (React unmounts the tree on an uncaught
 * throw). `resetKey` (e.g. the current pathname) clears the error when it changes, so
 * navigating away from a broken screen recovers without a full reload.
 */
interface Props {
  children: ReactNode;
  resetKey?: string;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Screen error:', error, info.componentStack);
  }

  override componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="cl col" style={{ padding: 32, gap: 12, maxWidth: 720 }}>
        <div className="h2" style={{ color: 'var(--red)' }}>
          Something went wrong on this screen
        </div>
        <div className="meta" style={{ fontSize: 14 }}>
          The page hit an unexpected error. Try reloading; if it persists, share this
          message with support.
        </div>
        <pre
          className="mono"
          style={{
            whiteSpace: 'pre-wrap',
            fontSize: 13,
            background: 'var(--card-2)',
            border: '1px solid var(--line)',
            borderRadius: 6,
            padding: 12,
            overflow: 'auto',
          }}
        >
          {error.message}
        </pre>
        <div className="row gap-8">
          <button
            type="button"
            className="btn primary"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
